import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '../../lib/utils'

const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-sm border font-medium whitespace-nowrap',
  {
    variants: {
      /* `tag` is the 10px uppercase chip: PROD, a channel, a platform. Its type
         lives here rather than as a `t-tag` className at the call site, because
         a utility cannot reliably beat this component's own `text-xs`. */
      size: {
        default: 'px-2 py-0.5 text-xs',
        tag: 'px-1.5 py-0.5 t-tag',
      },
      variant: {
        neutral: 'border-transparent bg-bg-subtle text-fg-muted',
        success: 'border-transparent bg-ok-surface text-ok',
        warning: 'border-transparent bg-warn-surface text-warn',
        danger: 'border-transparent bg-err-surface text-err',
        outline: 'border-border bg-bg-elev text-fg-muted',
      },
    },
    defaultVariants: { variant: 'neutral', size: 'default' },
  },
)

interface BadgeProps extends React.ComponentProps<'span'>, VariantProps<typeof badgeVariants> {
  /** Render a leading status dot in the badge's own colour. */
  dot?: boolean
}

function Badge({ className, variant, size, dot, children, ...props }: BadgeProps) {
  return (
    <span data-slot="badge" className={cn(badgeVariants({ variant, size, className }))} {...props}>
      {dot && <span aria-hidden className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  )
}

export { Badge, badgeVariants, type BadgeProps }
